/**
 * Attendant-triggered live vendor lookup offered on a refused lane card (CWS-1266).
 *
 * The LPR gate no longer asks the vendor (DRB) itself; it refuses at once and
 * the card carries a button. These helpers decide when to show it, what to call
 * it, and how to word the answer. Pure functions, no Angular, so they are cheap
 * to test and the modal stays thin.
 *
 * Three cards can carry the offer; the attendant sees one button, "Check with DRB":
 *   refresh  create-order-request, member whose stored membership reads expired
 *   check    create-order-request, vendor customer we hold no membership for
 *   lookup   create-customer-request / invalid-license-plate, plate unknown to us
 */
import type { NotificationData } from '../../services/notification.service';
import type { VendorMembershipLookupData, VendorMembershipLookupRequest } from '../types';

export type VendorMembershipLookupKind = 'refresh' | 'check' | 'lookup';

export interface VendorMembershipLookupOffer {
  /** `customers.source` value, e.g. 'drb' */
  vendor: string;
  /** Attendant-facing vendor name, e.g. 'DRB' */
  label: string;
  kind: VendorMembershipLookupKind;
}

export interface VendorLookupResultText {
  severity: 'success' | 'warn' | 'error';
  text: string;
}

/**
 * Backend copy for a vendor member whose membership reads expired carries this
 * phrase (svc-order constants/lprRefusalMessages). It is the fallback when a row
 * was re-read from the list and only `message` survived.
 */
export const VENDOR_EXPIRED_MESSAGE_MARKER = /expired in our records/i;

/** "Check with DRB" inside that copy names the vendor for the fallback path. */
const CHECK_WITH_TOKEN = /check with ([A-Za-z0-9]+)/i;

const VENDOR_LABELS: Readonly<Record<string, string>> = Object.freeze({
  drb: 'DRB',
  washify: 'Washify',
  everwash: 'EverWash',
  iwashpos: 'iWashPOS',
});

/** Vendors offered on an unknown-plate card when the shop has them enabled. */
const UNKNOWN_PLATE_LOOKUP_VENDORS: readonly string[] = Object.freeze(['drb']);

const UNKNOWN_PLATE_EVENTS = new Set<string>(['create-customer-request', 'invalid-license-plate']);

/** Outcomes/reasons after which pressing again can reasonably succeed. */
const RETRYABLE_NOT_APPLIED_REASONS = new Set<string>([
  'no_entitlement_evidence',
  'no_answer',
  'plan_data_warming',
]);

export function vendorLookupLabel(source: string | null | undefined): string {
  const key = String(source ?? '')
    .trim()
    .toLowerCase();
  return VENDOR_LABELS[key] ?? key.toUpperCase();
}

function sourceForLabel(label: string): string | null {
  const wanted = label.trim().toLowerCase();
  const hit = Object.entries(VENDOR_LABELS).find(([, l]) => l.toLowerCase() === wanted);
  return hit ? hit[0] : null;
}

/** SSE payloads nest under `data`; list rows may nest once more under `data.data`. */
function flattenNotificationData(
  notification: Pick<NotificationData, 'data'> | null | undefined
): Record<string, any> {
  const outer = (notification?.data ?? {}) as Record<string, any>;
  const inner = (outer['data'] ?? {}) as Record<string, any>;
  return { ...inner, ...outer };
}

/**
 * The card's messages, outermost first. A live SSE card carries the refusal copy
 * at the top level; a rehydrated or re-labelled row carries the generic event
 * text there and the refusal copy under `data.message`, so callers must look at
 * every one rather than the first that is non-empty.
 */
function messagesOf(notification: Pick<NotificationData, 'message' | 'data'>): string[] {
  const data = flattenNotificationData(notification);
  return [notification.message, data['message']]
    .filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
    .map(m => m.trim());
}

function offer(vendor: string, kind: VendorMembershipLookupKind): VendorMembershipLookupOffer {
  return { vendor, label: vendorLookupLabel(vendor), kind };
}

/**
 * Whether this card should carry the vendor lookup, and which flavour.
 *
 * @param notification The card's notification
 * @param isVendorEnabled Shop-level enablement, normally `VendorModeService.isVendorSourceEnabled`
 */
export function resolveVendorMembershipLookupOffer(
  notification: Pick<NotificationData, 'type' | 'message' | 'data'> | null | undefined,
  isVendorEnabled: (source: string) => boolean
): VendorMembershipLookupOffer | null {
  if (!notification) {
    return null;
  }
  const data = flattenNotificationData(notification);
  if (data['is_completed'] === true) {
    return null;
  }
  // A card that already carries an order has nothing left to look up: the wash
  // is sold, and svc-order refuses the row as no longer a refusal (409).
  if (String(data['order_id'] ?? data['orderId'] ?? '').trim()) {
    return null;
  }

  // Structured hint from the refusal (preferred).
  const hint = data['vendor_lookup'] as { available?: boolean; vendor?: string } | undefined;
  if (hint?.available === true && typeof hint.vendor === 'string' && hint.vendor) {
    const vendor = hint.vendor.toLowerCase();
    if (!isVendorEnabled(vendor)) {
      return null;
    }
    const kind: VendorMembershipLookupKind =
      data['refusal_reason'] === 'no_vendor_membership' ? 'check' : 'refresh';
    return offer(vendor, kind);
  }

  const type = String(notification.type ?? '');

  if (type === 'create-order-request') {
    // Rehydrated row: only the message survived. The marker + vendor token is
    // enough to offer the refresh.
    const message = messagesOf(notification).find(m => VENDOR_EXPIRED_MESSAGE_MARKER.test(m));
    if (message) {
      const token = CHECK_WITH_TOKEN.exec(message)?.[1];
      const vendor = token ? sourceForLabel(token) : null;
      if (vendor && isVendorEnabled(vendor)) {
        return offer(vendor, 'refresh');
      }
    }
    return null;
  }

  if (UNKNOWN_PLATE_EVENTS.has(type)) {
    const vendor = UNKNOWN_PLATE_LOOKUP_VENDORS.find(v => isVendorEnabled(v));
    return vendor ? offer(vendor, 'lookup') : null;
  }

  return null;
}

/** The request body for POST /order/lpr-entries/:id/vendor-membership-lookup. */
export function buildVendorLookupRequest(
  data: Record<string, any> | null | undefined,
  fallbackLocationId: string | null | undefined
): VendorMembershipLookupRequest | null {
  const flat = flattenNotificationData({ data: data ?? undefined });
  const locationId = String(
    flat['location_id'] ?? flat['locationId'] ?? fallbackLocationId ?? ''
  ).trim();
  if (!locationId) {
    return null;
  }
  const roi = flat['camera_roi'] ?? flat['roiID'] ?? flat['roi_id'] ?? null;
  const roiId = roi === null || roi === undefined || String(roi).trim() === '' ? null : String(roi);
  return roiId ? { location_id: locationId, roi_id: roiId } : { location_id: locationId };
}

/**
 * Banner sentence for a card that has no greeting of its own but carries the lookup:
 * a vendor customer we hold no membership row for. The expired card already carries
 * the backend copy, and the unknown-plate card keeps its welcome tip.
 */
export function vendorLookupOfferMessage(offer: VendorMembershipLookupOffer | null): string | null {
  if (!offer || offer.kind !== 'check') {
    return null;
  }
  return `We don't have a ${offer.label} membership on file for this customer. Check with ${offer.label}, or create a new order.`;
}

/**
 * CWS-1828: the ask that follows the welcome tip on a card whose status strip says the
 * vendor membership is inactive. The strip says why; this sends the attendant to the vendor
 * whose lookup button sits beside it.
 *
 * @param label the offered lookup's vendor label, e.g. 'DRB'
 */
export function vendorPaidRecentlyHint(label: string): string {
  return `Check with ${label} in case they've paid recently.`;
}

export function vendorLookupButtonLabel(
  offer: VendorMembershipLookupOffer,
  running: boolean
): string {
  return running ? `Checking with ${offer.label}…` : `Check with ${offer.label}`;
}

/**
 * Attendant-facing rendering of the backend outcome: two lines only. DRB said
 * yes → redeemed; anything else → "we couldn't find". Prefers the backend's own
 * sentence when it sent one.
 */
export function mapVendorLookupOutcomeToText(
  data: VendorMembershipLookupData | null | undefined,
  label: string
): VendorLookupResultText {
  const backendText = typeof data?.message === 'string' ? data.message.trim() : '';
  if (data?.outcome === 'redeemed') {
    return {
      severity: 'success',
      text:
        backendText ||
        `${label} confirmed the membership. The wash is redeemed and the lane is opening.`,
    };
  }
  return { severity: 'warn', text: backendText || vendorLookupNotFoundText(label) };
}

/** The one line for every miss, including a request that failed outright. */
export function vendorLookupNotFoundText(label: string): string {
  return `We couldn't find an active ${label} membership for this vehicle.`;
}

/** Keep the button after a transient failure; hide it once the vendor has actually said no. */
export function isVendorLookupRetryable(
  data: VendorMembershipLookupData | null | undefined
): boolean {
  if (!data) {
    return true;
  }
  if (data.outcome === 'unavailable') {
    return true;
  }
  return (
    data.outcome === 'not_applied' && RETRYABLE_NOT_APPLIED_REASONS.has(String(data.reason ?? ''))
  );
}

/**
 * Shape the returned LPR row like a PUT /order/lpr-entries/:id response, so the
 * existing in-place list/modal sync can consume it unchanged.
 */
export function toPutShapedLprEntryResponse(
  lprEntry: Record<string, any> | null | undefined
): { status: true; status_code: 200; data: Record<string, any> } | null {
  if (!lprEntry || typeof lprEntry !== 'object' || Array.isArray(lprEntry)) {
    return null;
  }
  return { status: true, status_code: 200, data: lprEntry };
}
